import { DataCollectionField, QualificationQuestion } from './types';

/**
 * Motor Vehicle Accident (MVA) Qualification Questions
 *
 * These 6 questions determine if a case should be signed.
 * Each question has:
 * - A yes/no/unknown answer
 * - A context prompt for the operator to ask the client
 * - Expected answer for qualification
 * - Whether it's a disqualifier
 */
export const MVA_QUALIFICATION_QUESTIONS: QualificationQuestion[] = [
  // Order follows reading pattern: left-to-right, top-to-bottom
  // This guides intakers through questions in the optimal sequence
  {
    id: 'existing_attorney',
    text: 'Are you currently represented by another attorney for this matter?',
    contextPrompt: 'Have you signed with any other law firm for this accident?',
    isDisqualifier: true,
    expectedAnswer: false, // No = qualified (they should NOT have an attorney)
    order: 1,
    noteOptional: true, // "No" needs no explanation
  },
  {
    id: 'injured',
    text: 'Were you injured in the accident?',
    contextPrompt: 'What injuries did you sustain from the accident?',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 2,
    noteFormDataPath: 'incident_info.present_complaints', // Sync injury details to Review screen
  },
  {
    id: 'medical_treatment',
    text: 'Did you seek medical treatment within 30 days of your accident (or plan to)?',
    contextPrompt:
      'Have you seen any doctors since the accident, or do you plan to?',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 3,
  },
  {
    id: 'at_fault',
    text: 'Were you at fault for the accident?',
    contextPrompt: 'Can you tell me what happened? Who caused the accident?',
    isDisqualifier: true,
    expectedAnswer: false, // No = qualified (they should NOT be at fault)
    order: 4,
  },
  {
    id: 'insurance_path',
    text: 'Is there an insurance path for recovery?',
    contextPrompt:
      'Do you know if the other driver has insurance? Do you have your own auto insurance?',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 5,
  },
];

/**
 * Premises Liability Qualification Questions
 *
 * These 8 questions determine if a premises liability case should be signed.
 * Each question has:
 * - A yes/no/unknown answer
 * - A context prompt for the operator to ask the client
 * - Expected answer for qualification
 * - Whether it's a disqualifier
 */
export const PREMISES_QUALIFICATION_QUESTIONS: QualificationQuestion[] = [
  // Order follows reading pattern: left-to-right, top-to-bottom
  // All IDs prefixed with 'pl_' to prevent cross-contamination with MVA questions
  {
    id: 'pl_existing_attorney',
    text: 'Are you currently represented by another attorney for this matter?',
    contextPrompt: 'Have you signed with any other law firm for this incident?',
    isDisqualifier: true,
    expectedAnswer: false, // No = qualified (they should NOT have an attorney)
    order: 1,
    noteOptional: true,
  },
  {
    id: 'pl_injured',
    text: 'Were you injured in the accident?',
    contextPrompt:
      'Make sure to detail the specific injuries and whether they are significant.',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 2,
    noteFormDataPath: 'incident_info.present_complaints',
  },
  {
    id: 'pl_slip_trip_fall',
    text: "Was your injury caused by a slip, trip, fall, or hazardous condition on someone else's property?",
    contextPrompt: "Document exactly whose property it is/was.",
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 3,
  },
  {
    id: 'pl_lawfully_present',
    text: 'Were you lawfully present on the property when the incident occurred?',
    contextPrompt:
      'What was your purpose for being on the property? Were you invited or a customer?',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 4,
  },
  {
    id: 'pl_identify_cause',
    text: 'Can you identify what caused your fall or injury?',
    contextPrompt:
      'Are there pictures, videos, or witnesses to the accident and accident location?',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 5,
  },
  {
    id: 'pl_hazard_marked',
    text: 'Was the hazard clearly marked?',
    contextPrompt:
      'Focus on whether the property owner should have known about the hazard.',
    isDisqualifier: true,
    expectedAnswer: false, // No = qualified (hazard should NOT be marked)
    order: 6,
  },
  {
    id: 'pl_medical_treatment',
    text: 'Did you seek medical treatment within 30 days of your accident (or plan to)?',
    contextPrompt:
      'Have you seen any doctors since the incident, or do you plan to?',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 7,
  },
  {
    id: 'pl_insurance_path',
    text: 'Is there an insurance or recovery path?',
    contextPrompt:
      'Is the property owned by a business or individual with insurance? Is there a way to identify the responsible party?',
    isDisqualifier: true,
    expectedAnswer: true, // Yes = qualified
    order: 8,
  },
];

/**
 * Get qualification questions for a case type.
 */
export const getQualificationQuestions = (
  caseType: string | null | undefined
): QualificationQuestion[] => {
  const normalizedType = caseType?.toLowerCase().replace(/\s+/g, '_');

  switch (normalizedType) {
    // Motor Vehicle Accident - handles various formats
    case 'motor_vehicle':
    case 'motor_vehicle_accident':
    case 'auto_accident': // API format
    case 'mva':
      return MVA_QUALIFICATION_QUESTIONS;

    // Premises Liability - handles various formats
    case 'premise_liability': // API format
    case 'premises_liability':
      return PREMISES_QUALIFICATION_QUESTIONS;

    // Mold - no qualification questions (data collection only)
    case 'mold':
      return [];

    default:
      // Return empty array for unsupported case types
      return [];
  }
};

// ---------------------------------------------------------------------------
// Screen 2: Data Collection Fields
// ---------------------------------------------------------------------------

export const MVA_DATA_COLLECTION_FIELDS: DataCollectionField[] = [
  // INCIDENT_DETAILS
  {
    id: 'accident_date',
    label: 'Accident Date',
    description: 'What date did this happen?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'accident_location',
    label: 'Location',
    description: 'Where did the accident occur?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'police_report',
    label: 'Police Report',
    description: 'Was a police report filed?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'accident_description',
    label: 'What Happened',
    description: 'Can you describe what happened?',
    category: 'INCIDENT_DETAILS',
  },

  // INSURANCE_INFO
  {
    id: 'defendant_insurance',
    label: '3P Insurance',
    description: "Do you know the other driver's insurance?",
    category: 'INSURANCE_INFO',
  },
  {
    id: 'client_insurance',
    label: 'Your Insurance',
    description: 'Who is your auto insurance provider?',
    category: 'INSURANCE_INFO',
  },
  {
    id: 'claim_number',
    label: 'Claim Number',
    description: "Has a claim been filed? What's the number?",
    category: 'INSURANCE_INFO',
  },

  // MEDICAL_TREATMENT
  {
    id: 'injuries',
    label: 'Injuries',
    description: 'What injuries did you sustain?',
    category: 'MEDICAL_TREATMENT',
  },
  {
    id: 'er_visit',
    label: 'ER Visit',
    description: 'Did you go to the emergency room?',
    category: 'MEDICAL_TREATMENT',
  },
  {
    id: 'current_treatment',
    label: 'Current Treatment',
    description: 'Are you currently seeing any doctors?',
    category: 'MEDICAL_TREATMENT',
  },

  // PERSONAL_INFO
  {
    id: 'dob',
    label: 'Date of Birth',
    description: 'What is your date of birth?',
    category: 'PERSONAL_INFO',
  },
  {
    id: 'address',
    label: 'Address',
    description: 'What is your current address?',
    category: 'PERSONAL_INFO',
  },

  // DEFENDANT_INFO
  {
    id: 'defendant_name',
    label: 'Other Driver',
    description: "Do you know the other driver's name?",
    category: 'DEFENDANT_INFO',
  },
  {
    id: 'defendant_vehicle',
    label: 'Other Vehicle',
    description: 'What kind of car was the other driver in?',
    category: 'DEFENDANT_INFO',
  },
];

// ---------------------------------------------------------------------------
// Premises Liability Data Collection Fields
// ---------------------------------------------------------------------------

export const PREMISES_DATA_COLLECTION_FIELDS: DataCollectionField[] = [
  // INCIDENT_DETAILS
  {
    id: 'pl_incident_date',
    label: 'Incident Date',
    description: 'What date did this happen?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'pl_incident_location',
    label: 'Location on Property',
    description: 'Where exactly on the property did the incident occur?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'pl_incident_description',
    label: 'What Happened',
    description: 'Can you describe what happened?',
    category: 'INCIDENT_DETAILS',
  },

  // PROPERTY_INFO
  {
    id: 'pl_property_owner',
    label: 'Property Owner',
    description: 'Who owns or manages the property?',
    category: 'DEFENDANT_INFO',
  },
  {
    id: 'pl_property_type',
    label: 'Property Type',
    description: 'What type of property was it?',
    category: 'DEFENDANT_INFO',
  },

  // MEDICAL_TREATMENT
  {
    id: 'pl_injuries',
    label: 'Injuries',
    description: 'What injuries did you sustain?',
    category: 'MEDICAL_TREATMENT',
  },
  {
    id: 'pl_er_visit',
    label: 'ER Visit',
    description: 'Did you go to the emergency room?',
    category: 'MEDICAL_TREATMENT',
  },
  {
    id: 'pl_current_treatment',
    label: 'Current Treatment',
    description: 'Are you currently seeing any doctors?',
    category: 'MEDICAL_TREATMENT',
  },

  // PERSONAL_INFO
  {
    id: 'pl_dob',
    label: 'Date of Birth',
    description: 'What is your date of birth?',
    category: 'PERSONAL_INFO',
  },
  {
    id: 'pl_address',
    label: 'Address',
    description: 'What is your current address?',
    category: 'PERSONAL_INFO',
  },

  // INSURANCE_INFO
  {
    id: 'pl_health_insurance',
    label: 'Health Insurance',
    description: 'Who is your health insurance provider?',
    category: 'INSURANCE_INFO',
  },
];

// ---------------------------------------------------------------------------
// Mold Data Collection Fields
// ---------------------------------------------------------------------------

export const MOLD_DATA_COLLECTION_FIELDS: DataCollectionField[] = [
  // MEDICAL_TREATMENT
  {
    id: 'mold_injuries',
    label: 'Injuries/Ailments',
    description: 'What injuries or ailments do you have from mold?',
    category: 'MEDICAL_TREATMENT',
  },
  {
    id: 'mold_doctors',
    label: 'Doctors Seen',
    description: 'What doctors have you seen for mold? What is your diagnosis?',
    category: 'MEDICAL_TREATMENT',
  },

  // INCIDENT_DETAILS
  {
    id: 'mold_cause',
    label: 'Cause of Mold',
    description: 'What caused the mold and when?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'mold_landlord_knowledge',
    label: 'Landlord Knowledge',
    description: 'Did your landlord know about mold and fail to fix it?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'mold_test',
    label: 'Mold Test',
    description: 'Did you have a mold test completed with air sample testing?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'mold_photos',
    label: 'Photographs',
    description: 'Do you have photographs of the mold and injuries?',
    category: 'INCIDENT_DETAILS',
  },
  {
    id: 'mold_communications',
    label: 'Written Communications',
    description: 'Do you have emails, texts, or letters to landlord/property manager about the mold?',
    category: 'INCIDENT_DETAILS',
  },

  // DEFENDANT_INFO
  {
    id: 'mold_landlord',
    label: 'Landlord/Property Manager',
    description: 'Who is your landlord and property manager?',
    category: 'DEFENDANT_INFO',
  },
  {
    id: 'mold_hoa',
    label: 'HOA',
    description: 'Do you have an HOA?',
    category: 'DEFENDANT_INFO',
  },

  // INSURANCE_INFO
  {
    id: 'mold_property_damage',
    label: 'Property Damage',
    description: 'Do you have property damage? How much is your property loss?',
    category: 'INSURANCE_INFO',
  },

  // PERSONAL_INFO
  {
    id: 'mold_residence_status',
    label: 'Residence Status',
    description: 'Are you still living at the subject residence? Have you moved?',
    category: 'PERSONAL_INFO',
  },
];

/**
 * Get data collection fields for a case type.
 */
export const getDataCollectionFields = (
  caseType: string | null | undefined
): DataCollectionField[] => {
  const normalizedType = caseType?.toLowerCase().replace(/\s+/g, '_');

  switch (normalizedType) {
    case 'motor_vehicle':
    case 'motor_vehicle_accident':
    case 'auto_accident':
    case 'mva':
      return MVA_DATA_COLLECTION_FIELDS;

    case 'premise_liability':
    case 'premises_liability':
      return PREMISES_DATA_COLLECTION_FIELDS;

    case 'mold':
      return MOLD_DATA_COLLECTION_FIELDS;

    default:
      return MVA_DATA_COLLECTION_FIELDS; // Fallback to MVA for now
  }
};
